import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { SpeechState, SpeechTranscriptPayload } from '../types';

const initialState: SpeechState = {
  isListening: false,
  isConnecting: false,
  error: null,
  isEnabled: false,
  currentTranscript: '',
  isProcessingTranscript: false,
  lastTranscriptTime: null,
  transcriptQueue: [],
  isCollectingTranscripts: false,
  isCollectingInterruption: false,
  availableDevices: [],
  selectedDeviceId: 'default',
  isLoadingDevices: false,
};

const speechSlice = createSlice({
  name: 'speech',
  initialState,
  reducers: {
    // Simple state setters
    setEnabled: (state, action: PayloadAction<boolean>) => {
      console.log('🎤 Speech setEnabled:', action.payload);
      state.isEnabled = action.payload;
      if (!action.payload) {
        // Reset everything if disabled
        state.isListening = false;
        state.isConnecting = false;
        state.currentTranscript = '';
        state.isProcessingTranscript = false;
        state.error = null;
      }
    },
    
    setListening: (state, action: PayloadAction<boolean>) => {
      console.log('🎤 Speech setListening:', action.payload);
      state.isListening = action.payload;
      if (!action.payload) {
        state.currentTranscript = '';
        state.isProcessingTranscript = false;
      }
    },
    
    setConnecting: (state, action: PayloadAction<boolean>) => {
      console.log('🎤 Speech setConnecting:', action.payload);
      state.isConnecting = action.payload;
    },
    
    setError: (state, action: PayloadAction<string>) => {
      console.log('🎤 Speech setError:', action.payload);
      state.error = action.payload;
      state.isListening = false;
      state.isConnecting = false;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    // Toggle action that middleware will handle
    toggle: (state) => {
      console.log('🎤 Speech toggle action');
      // State changes will be handled by middleware
    },

    connectSpeech: (state) => {
      console.log('🎤 Speech connectSpeech');
    } ,

    disconnectSpeech: (state) => {
      console.log('🎤 Speech disconnectSpeech');
    },

    
    // Transcript handling
    transcriptReceived: (state, action: PayloadAction<SpeechTranscriptPayload>) => {
      const { transcript, isFinal } = action.payload;
      state.currentTranscript = transcript;
      state.lastTranscriptTime = Date.now();
      state.isProcessingTranscript = false;
    },
    
    clearTranscript: (state) => {
      state.currentTranscript = '';
      state.isProcessingTranscript = false;
    },

    addToTranscriptQueue: (state, action: PayloadAction<string>) => {
      console.log('Adding to transcript queue:', action.payload);
      state.transcriptQueue.push(action.payload);
      state.isCollectingTranscripts = true;
    },
    
    clearTranscriptQueue: (state) => {
      console.log('Clearing transcript queue');
      state.transcriptQueue = [];
      state.isCollectingTranscripts = false;
    },

    utteranceEnd: (state) => {
      console.log('🎤 Speech utteranceEnd action');
      console.log('State: ', state);
      // The actual processing is handled by middleware
      // This just marks that utterance ended
    },

    startInterruption: (state) => {
      console.log('🎤 Starting interruption collection');
      state.isCollectingInterruption = true;
    },
    
    endInterruption: (state) => {
      console.log('🎤 Ending interruption collection');
      state.isCollectingInterruption = false;
    },

    // Device management actions
    setLoadingDevices: (state, action: PayloadAction<boolean>) => {
      state.isLoadingDevices = action.payload;
    },

    setAvailableDevices: (state, action: PayloadAction<MediaDeviceInfo[]>) => {
      state.availableDevices = action.payload;
      state.isLoadingDevices = false;
    },

    setSelectedDevice: (state, action: PayloadAction<string>) => {
      console.log('🎤 Setting selected device:', action.payload);
      state.selectedDeviceId = action.payload;
    },

    loadDevices: (state) => {
      console.log('🎤 Loading audio devices');
    },
    
    // Reset everything
    reset: (state) => {
      console.log('🎤 Speech reset');
      state.isListening = false;
      state.isConnecting = false;
      state.error = null;
      state.currentTranscript = '';
      state.isProcessingTranscript = false;
      state.lastTranscriptTime = null;
    },
  },
});

export const {
  setEnabled,
  setListening,
  setConnecting,
  setError,
  clearError,
  toggle,
  transcriptReceived,
  clearTranscript,
  addToTranscriptQueue,
  clearTranscriptQueue,
  utteranceEnd,
  startInterruption,
  endInterruption,
  setLoadingDevices,
  setAvailableDevices,
  setSelectedDevice,
  loadDevices,
  reset,
  connectSpeech,
  disconnectSpeech, 
} = speechSlice.actions;

export default speechSlice.reducer;